import { type KeyboardEvent, useState } from "react";
import { LuSendHorizontal } from "react-icons/lu";
import IconButton from "../../../../components/icon-button/icon-button";
import Input from "../../../../components/input/input";
import { useCurrentUser } from "../../../../hooks/use-current-user";
import { authFetch } from "../../../../lib/auth-fetch";
import * as CSS from "./viewer-comment-bar.styles";

type ViewerCommentBarProps = {
	broadcastId: string | null;
	onCommentSent: () => void;
};

const ViewerCommentBar = ({
	broadcastId,
	onCommentSent,
}: ViewerCommentBarProps) => {
	const currentUser = useCurrentUser();
	const [text, setText] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const isLoggedIn = !!currentUser;
	const canSend = isLoggedIn && !!broadcastId && !!text.trim() && !submitting;

	const handleSend = async () => {
		const trimmed = text.trim();
		if (!isLoggedIn || !broadcastId || !trimmed || submitting) return;

		setSubmitting(true);
		try {
			const res = await authFetch(`/api/broadcasts/${broadcastId}/comments`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					text: trimmed,
					userName: currentUser.name ?? currentUser.email,
				}),
			});
			if (res.ok) {
				setText("");
				onCommentSent();
			}
		} catch {
			// swallow — the input keeps its text so the user can retry
		} finally {
			setSubmitting(false);
		}
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			handleSend();
		}
	};

	return (
		<CSS.ViewerCommentBar>
			<Input
				value={text}
				onChange={(e) => setText(e.target.value)}
				onKeyDown={handleKeyDown}
				disabled={!isLoggedIn}
				placeholder={
					isLoggedIn ? "Type comment here" : "Login to join the conversation"
				}
			/>
			<IconButton
				icon={LuSendHorizontal}
				onClick={handleSend}
				disabled={!canSend}
			/>
		</CSS.ViewerCommentBar>
	);
};

export default ViewerCommentBar;
